import {
  Component,
  OnInit,
  TemplateRef,
  Input,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import {
  BsModalRef,
  BsModalService,
  ModalDirective,
} from 'ngx-bootstrap/modal';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { AdminService } from '../../services/admin/admin.service';
import { HelpersService } from '../../../shared/servicio/helpers/helpers.service';

// Interfaces locales
interface CreateQuestionRequest {
  survey_id: number;
  items: Array<{
    question: string;
    question_type: 'closed' | 'open' | 'categorized';
    answers?: string[];
    specialty_filter?: string[];
  }>;
}

// Constantes para estados de guardado
const GUARDADO = 1;
const GUARDANDO = 2;
const ERROR_GUARDADO = 3;

@Component({
  selector: 'app-create-questions',
  templateUrl: './create-questions.component.html',
  styleUrls: ['./create-questions.component.css'],
})
export class CreateQuestionsComponent implements OnInit, OnDestroy {
  @Input() surveyId: number;
  @Input() modalRef: BsModalRef;

  // Estados de guardado
  guardando: number = GUARDADO;
  readonly GUARDADO = GUARDADO;
  readonly GUARDANDO = GUARDANDO;
  readonly ERROR_GUARDADO = ERROR_GUARDADO;

  // Tipos de preguntas
  questionTypes = [
    { value: 'closed', label: 'Cerrada' },
    { value: 'open', label: 'Abierta' },
    { value: 'categorized', label: 'Categorizada por especialidad' }
  ];

  // Datos de la encuesta
  name_surveys: string = '';
  description_surveys: string = '';

  // Controles de formulario
  tituloFormControl: FormControl;
  descripcionFormControl: FormControl;
  preguntasListFormControls: { [key: string]: FormControl } = {};
  preguntasTypeFormControls: { [key: string]: FormControl } = {};
  respuestasListFormControls: { [key: string]: Array<{ id: any; control: FormControl }> } = {};

  // Gestión de preguntas
  preguntasIds: any[] = [];
  tempId: number = 0;
  tempAnswerId: number = 0;
  submitted: boolean = false;
  showLoading: boolean = false;

  // Configuración de API
  useNewApi: boolean = true;

  // Control de envíos múltiples
  private sendingQuestions = new Set<string>();
  private questionDebounceTimers: { [key: string]: any } = {};
  private newQuestionTimers: { [key: string]: any } = {};
  private descriptionDebounceTimer: any;

  constructor(
    private modalService: BsModalService,
    private formBuilder: FormBuilder,
    public admin: AdminService,
    public helper: HelpersService
  ) { }

  ngOnInit() {
    this.initializeComponent();
    this.loadQuestions();
  }

  // Control de estados de guardado
  private saveStateTimer: any;

  /**
   * Resetea el estado de guardado después de un tiempo sin actividad
   */
  private resetSaveStateAfterDelay(delay: number = 3000) {
    if (this.saveStateTimer) {
      clearTimeout(this.saveStateTimer);
    }

    this.saveStateTimer = setTimeout(() => {
      if (this.guardando === GUARDANDO) {
        console.log('Reseteando estado de guardado por timeout');
        this.guardando = GUARDADO;
      }
    }, delay);
  }

  ngOnDestroy() {
    // Limpiar todos los timers
    Object.values(this.questionDebounceTimers).forEach(timer => {
      if (timer) clearTimeout(timer);
    });

    Object.values(this.newQuestionTimers).forEach(timer => {
      if (timer) clearTimeout(timer);
    });

    if (this.descriptionDebounceTimer) {
      clearTimeout(this.descriptionDebounceTimer);
    }

    if (this.saveStateTimer) {
      clearTimeout(this.saveStateTimer);
    }

    // Limpiar sets de control
    this.sendingQuestions.clear();
  }

  /**
   * Inicializa el componente
   */
  private initializeComponent() {
    this.showLoading = true;
    this.guardando = GUARDADO;
    this.tempId = 0;
    this.tempAnswerId = 0;
    this.preguntasListFormControls = {};
    this.preguntasTypeFormControls = {};
    this.preguntasIds = [];
    this.respuestasListFormControls = {};
    this.tituloFormControl = new FormControl();
    this.descripcionFormControl = new FormControl();
    this.sendingQuestions.clear();
  }

  /**
   * Carga las preguntas de la encuesta
   */
  loadQuestions() {
    const loadMethod = this.useNewApi ?
      this.admin.getSurveyDetailsNew(this.surveyId) :
      this.admin.list_idSurvey(this.surveyId);

    loadMethod.subscribe({
      next: (data: any) => {
        this.handleQuestionsLoaded(data);
      },
      error: (error) => {
        console.error('Error loading questions:', error);
        this.modalRef.hide();
        this.helper.modalAlert('¡No se pudo cargar la encuesta!');
      }
    });
  }

  /**
   * Maneja la respuesta de carga de preguntas
   */
  private handleQuestionsLoaded(data: any) {
    console.log('Questions loaded:', data);

    // Limpiar preguntas temporales existentes
    this.cleanTempQuestions();

    // Configurar información de la encuesta
    this.setupSurveyInfo(data);

    // Procesar preguntas
    this.processQuestions(data.body || data);

    this.showLoading = false;
  }

  /**
   * Limpia las preguntas temporales
   */
  private cleanTempQuestions() {
    for (let i = 0; i < this.preguntasIds.length; i++) {
      if (!this.preguntasIds[i].toString().includes('temp')) {
        this.preguntasIds.splice(i, 1);
        i--;
      }
    }
  }

  /**
   * Configura la información de la encuesta
   */
  private setupSurveyInfo(data: any) {
    console.log('Raw data received:', data);

    // CORRECCIÓN: Acceso correcto según la estructura de tu JSON
    let survey;

    if (data.survey) {
      // Tu JSON tiene: {"survey": {"title": "encuesta"}}
      survey = data.survey;
    } else if (data.body && data.body.survey) {
      survey = data.body.survey;
    } else if (data.body) {
      survey = data.body;
    } else {
      survey = data;
    }

    // Extraer título y descripción
    this.name_surveys = survey.title || survey.name_survey || survey.name || 'Sin título';
    this.description_surveys = survey.description || survey.description_survey || survey.desc || '';

    console.log('Survey info extracted:', {
      title: this.name_surveys,
      description: this.description_surveys,
      surveyObject: survey
    });

    // Configurar controles con valores iniciales
    this.tituloFormControl = new FormControl(this.name_surveys, {
      updateOn: 'blur',
      validators: [Validators.required, Validators.minLength(5)]
    });

    this.descripcionFormControl = new FormControl(this.description_surveys, {
      updateOn: 'blur',
      validators: [Validators.minLength(10)]
    });

    // Configurar suscripciones después de inicializar
    setTimeout(() => {
      this.setupTitleSubscription();
      this.setupDescriptionSubscription();
    }, 100);
  }

  /**
   * Configura la suscripción para cambios en el título
   */
  private setupTitleSubscription() {
    this.tituloFormControl.valueChanges.subscribe((value) => {
      this.guardando = GUARDANDO;
      this.changeTitle(value);
    });
  }

  /**
   * Configura la suscripción para cambios en la descripción
   */
  private setupDescriptionSubscription() {
    this.descripcionFormControl.valueChanges.subscribe((value) => {
      this.guardando = GUARDANDO;
      this.changeDescription(value);
    });
  }

  /**
   * Procesa las preguntas cargadas
   */
  private processQuestions(questions: any[]) {
    console.log('Processing questions:', questions);

    if (!Array.isArray(questions) || questions.length === 0) {
      console.log('No hay preguntas existentes, iniciando con encuesta vacía');
      return;
    }

    questions.forEach((element) => {
      const id = element.question.id;
      this.preguntasIds.push(id);

      // Configurar control de pregunta
      this.setupQuestionControl(id, element.question.question);

      // Configurar control de tipo de pregunta
      this.setupQuestionTypeControl(id, element.question.question_type || 'closed');

      // Configurar controles de respuestas
      this.setupAnswerControls(id, element.answers || []);
    });
  }

  /**
   * Configura el control de pregunta
   */
  private setupQuestionControl(id: any, questionText: string) {
    this.preguntasListFormControls[id] = new FormControl(questionText, {
      validators: [Validators.required],
      updateOn: 'blur',
    });

    this.preguntasListFormControls[id].valueChanges.subscribe((value) => {
      const idStr = id.toString();

      if (idStr.includes('temp')) {
        // Para preguntas temporales, solo marcar como guardando si realmente va a enviar
        if (value && value.trim() !== '' && !this.sendingQuestions.has(idStr)) {
          this.guardando = GUARDANDO;
          this.debouncedSendNewQuestion(id, value);
        }
      } else {
        // Para preguntas existentes, solo si realmente hay cambio
        if (value && value.trim() !== '') {
          this.guardando = GUARDANDO;
          this.debouncedSendQuestion(id, value);
        } else {
          this.guardando = GUARDADO; // Resetear si no hay contenido
        }
      }
    });
  }

  /**
   * Configura el control del tipo de pregunta
   */
  private setupQuestionTypeControl(id: any, questionType: string) {
    this.preguntasTypeFormControls[id] = new FormControl(questionType, {
      validators: [Validators.required],
      updateOn: 'change',
    });

    this.preguntasTypeFormControls[id].valueChanges.subscribe((value) => {
      this.guardando = GUARDANDO;
      this.handleQuestionTypeChange(id, value);

      // CORRECCIÓN: No dejar timeout automático, el handleQuestionTypeChange maneja el estado
    });
  }

  /**
   * Envía cambio de tipo de pregunta al servidor
   */
  private sendQuestionTypeChangeToServer(questionId: any, questionType: string): void {
    // CORRECCIÓN: Usar timeout de respaldo
    this.resetSaveStateAfterDelay(5000);

    // Como no existe método específico para tipo, actualizar la pregunta completa
    const questionText = this.preguntasListFormControls[questionId].value;
    if (questionText && questionText.trim() !== '') {
      this.sendQuestion(questionId, questionText);
    } else {
      // Si no hay texto, simplemente marcar como guardado (cambio solo local)
      this.guardando = GUARDADO;
      if (this.saveStateTimer) clearTimeout(this.saveStateTimer);
    }
  }

  /**
   * Actualiza el ID de una respuesta después de ser creada
   */
  private updateAnswerIdAfterCreation(questionId: any, tempAnswerId: any, newAnswerId: any) {
    const answers = this.respuestasListFormControls[questionId];
    if (answers) {
      const answerToUpdate = answers.find(answer => answer.id === tempAnswerId);
      if (answerToUpdate) {
        answerToUpdate.id = newAnswerId;
        console.log(`Answer ID updated from ${tempAnswerId} to ${newAnswerId}`);
      }
    }
  }

  /**
   * Configura los controles de respuestas
   */
  private setupAnswerControls(id: any, answers: any[]) {
    this.respuestasListFormControls[id] = [];

    answers.forEach((ans) => {
      const control = new FormControl(ans.answer, {
        validators: [Validators.required],
        updateOn: 'blur',
      });

      const answer = { id: ans.id, control: control };
      this.respuestasListFormControls[id].push(answer);

      control.valueChanges.subscribe((value) => {
        this.guardando = GUARDANDO;
        this.sendAnswer(id, ans.id, value);
      });
    });
  }

  /**
   * Debounce para nuevas preguntas
   */
  private debouncedSendNewQuestion(id: string, questionText: string): void {
    if (this.newQuestionTimers[id]) {
      clearTimeout(this.newQuestionTimers[id]);
    }

    this.newQuestionTimers[id] = setTimeout(() => {
      if (!this.sendingQuestions.has(id.toString())) {
        this.sendNewQuestion(id);
      }
      delete this.newQuestionTimers[id];
    }, 1000);
  }

  /**
   * Debounce para preguntas existentes
   */
  private debouncedSendQuestion(id: any, value: string): void {
    if (this.questionDebounceTimers[id]) {
      clearTimeout(this.questionDebounceTimers[id]);
    }

    this.questionDebounceTimers[id] = setTimeout(() => {
      this.sendQuestion(id, value);
      delete this.questionDebounceTimers[id];
    }, 500);
  }

  /**
   * Cambia el título de la encuesta
   */
  changeTitle(title: string) {
    if (!title || title.trim() === '') return;

    const changeMethod = this.useNewApi ?
      this.admin.editSurveyTitleNew(this.surveyId, title) :
      this.admin.edit_survey_title(this.surveyId, title);

    changeMethod.subscribe({
      next: (data) => {
        this.guardando = GUARDADO;
      },
      error: (error) => {
        console.error('Error changing title:', error);
        this.guardando = ERROR_GUARDADO;
      }
    });
  }

  /**
   * Cambia la descripción de la encuesta
   */
  changeDescription(description: string) {
    if (this.descriptionDebounceTimer) {
      clearTimeout(this.descriptionDebounceTimer);
    }

    this.descriptionDebounceTimer = setTimeout(() => {
      if (!description) {
        this.guardando = GUARDADO;
        return;
      }

      const changeMethod = this.useNewApi ?
        this.admin.editSurveyDescriptionNew(this.surveyId, description) :
        this.admin.edit_survey_description(this.surveyId, description);

      changeMethod.subscribe({
        next: (data) => {
          console.log('Description changed successfully:', data);
          this.guardando = GUARDADO;
        },
        error: (error) => {
          console.error('Error changing description:', error);
          this.guardando = ERROR_GUARDADO;
          this.helper.modalAlert('Error al guardar la descripción');
        }
      });
    }, 800);
  }

  /**
   * Envía una nueva pregunta
   */
  sendNewQuestion(id: string) {
    const idStr = id.toString();

    // Validaciones previas
    if (this.sendingQuestions.has(idStr)) {
      console.log('Pregunta ya siendo enviada:', id);
      return;
    }

    const questionText = this.preguntasListFormControls[id].value;
    if (!questionText || questionText.trim() === '') {
      console.log('Pregunta vacía, no se envía');
      return;
    }

    // Marcar como enviándose
    this.sendingQuestions.add(idStr);

    try {
      const questionData = this.prepareQuestionData(id);
      console.log('Enviando pregunta:', questionData);

      const createMethod = this.useNewApi ?
        this.admin.addSurveyQuestionsNew(questionData) :
        this.admin.create_question(this.surveyId, questionData.items);

      createMethod.subscribe({
        next: (data) => {
          console.log('Pregunta creada exitosamente:', data);
          this.handleQuestionCreated(data, id);
          this.sendingQuestions.delete(idStr);
          this.guardando = GUARDADO;
        },
        error: (error) => {
          console.error('Error creating question:', error);
          this.sendingQuestions.delete(idStr);
          this.guardando = ERROR_GUARDADO;
          this.helper.modalAlert('Error al crear la pregunta');
        }
      });
    } catch (error) {
      console.error('Error preparando pregunta:', error);
      this.sendingQuestions.delete(idStr);
      this.guardando = ERROR_GUARDADO;
    }
  }

  /**
   * Prepara los datos para crear la pregunta
   */
  private prepareQuestionData(id: string): CreateQuestionRequest {
    const questionType = this.preguntasTypeFormControls[id].value || 'closed';
    const questionText = this.preguntasListFormControls[id].value;

    if (!questionText) {
      throw new Error('Texto de pregunta requerido');
    }

    const questionItem: any = {
      question: questionText.trim(),
      question_type: questionType
    };

    // Solo agregar respuestas si NO es pregunta abierta
    if (questionType !== 'open') {
      const answersControls = this.respuestasListFormControls[id] || [];
      const answers = answersControls
        .map(answer => answer.control.value)
        .filter(value => value && value.trim() !== '')
        .map(value => value.trim());

      // Validar que tenga al menos 2 respuestas para preguntas cerradas
      if (answers.length < 2) {
        // Si no tiene suficientes respuestas, usar valores por defecto
        questionItem.answers = ['Opción 1', 'Opción 2'];
      } else {
        questionItem.answers = answers;
      }
    }

    return {
      survey_id: this.surveyId,
      items: [questionItem]
    };
  }

  /**
   * Envía una pregunta modificada
   */
  sendQuestion(id: any, questionText: string) {
    if (!questionText || questionText.trim() === '') return;

    // CORRECCIÓN: Obtener el tipo de pregunta actual
    const questionType = this.preguntasTypeFormControls[id].value || 'closed';
    const questionId = typeof id === 'string' ? parseInt(id, 10) : id;

    const sendMethod = this.useNewApi ?
      this.admin.editSurveyQuestionNew(questionId, questionText, questionType) :
      this.admin.edit_survey_question(questionId, questionText, questionType);

    console.log('Enviando pregunta editada:', {
      questionId: questionId,
      questionText: questionText,
      questionType: questionType,
      useNewApi: this.useNewApi
    });

    sendMethod.subscribe({
      next: (data) => {
        console.log('Pregunta actualizada exitosamente:', data);
        this.guardando = GUARDADO;
        if (this.saveStateTimer) clearTimeout(this.saveStateTimer);
      },
      error: (error) => {
        console.error('Error sending question:', error);
        this.guardando = ERROR_GUARDADO;
        if (this.saveStateTimer) clearTimeout(this.saveStateTimer);
      }
    });
  }

  /**
   * Envía una respuesta modificada
   */
  sendAnswer(questionId: any, answerId: any, value: string) {
    if (!value || value.trim() === '') return;

    // CORRECCIÓN: No enviar respuestas para preguntas temporales
    if (questionId.toString().includes('temp')) {
      console.log('No se pueden crear respuestas para preguntas temporales:', questionId);
      // Solo marcar como que necesita guardado, pero no enviar
      this.guardando = GUARDANDO;
      return;
    }

    if (answerId.toString().includes('temp')) {
      // Respuesta nueva para pregunta existente
      const createMethod = this.useNewApi ?
        this.admin.addSurveyAnswerNew(questionId, [value]) :
        this.admin.create_survey_answer(questionId, [value]);

      createMethod.subscribe({
        next: (data) => {
          // CORRECCIÓN: Actualizar el ID en el control correspondiente
          this.updateAnswerIdAfterCreation(questionId, answerId, data.answer_ids[0] || data.id);
          this.guardando = GUARDADO;
        },
        error: (error) => {
          console.error('Error creating answer:', error);
          this.guardando = ERROR_GUARDADO;
        }
      });
    } else {
      // Respuesta existente
      const editMethod = this.useNewApi ?
        this.admin.editSurveyAnswerNew(answerId, value) :
        this.admin.edit_survey_answer(answerId, value);

      editMethod.subscribe({
        next: (data) => {
          this.guardando = GUARDADO;
        },
        error: (error) => {
          console.error('Error editing answer:', error);
          this.guardando = ERROR_GUARDADO;
        }
      });
    }
  }

  /**
   * Maneja la creación exitosa de una pregunta
   */
  private handleQuestionCreated(data: any, tempId: string) {
    const newId = data.ids ? data.ids[0].question_id : data.id;

    // Reemplazar ID temporal con ID real
    const index = this.preguntasIds.indexOf(tempId);
    if (index !== -1) {
      this.preguntasIds[index] = newId;
    }

    // Migrar controles
    this.migrateQuestionControls(tempId, newId);
    this.migrateQuestionTypeControls(tempId, newId);
    this.migrateAnswerControls(tempId, newId, data);
  }

  /**
   * Migra los controles de pregunta
   */
  private migrateQuestionControls(tempId: string, newId: any) {
    const control = this.preguntasListFormControls[tempId];
    const newControl = new FormControl(control.value, {
      validators: [Validators.required],
      updateOn: 'blur',
    });

    this.preguntasListFormControls[newId] = newControl;

    newControl.valueChanges.subscribe((value) => {
      this.guardando = GUARDANDO;
      this.debouncedSendQuestion(newId, value);
    });

    delete this.preguntasListFormControls[tempId];
  }

  /**
   * Migra los controles de tipo de pregunta
   */
  private migrateQuestionTypeControls(tempId: string, newId: any) {
    const control = this.preguntasTypeFormControls[tempId];
    const newControl = new FormControl(control.value, {
      validators: [Validators.required],
      updateOn: 'change',
    });

    this.preguntasTypeFormControls[newId] = newControl;

    newControl.valueChanges.subscribe((value) => {
      this.guardando = GUARDANDO;
      this.handleQuestionTypeChange(newId, value);
    });

    delete this.preguntasTypeFormControls[tempId];
  }

  /**
   * Migra los controles de respuestas
   */
  private migrateAnswerControls(tempId: string, newId: any, data: any) {
    const tempAnswers = this.respuestasListFormControls[tempId] || [];
    this.respuestasListFormControls[newId] = [];

    if (data.ids && data.ids[0].answers_ids) {
      // Si el servidor devuelve IDs de respuestas, usarlos
      data.ids[0].answers_ids.forEach((answerId: any, index: number) => {
        const tempAnswer = tempAnswers[index];
        const answerValue = tempAnswer ? tempAnswer.control.value : '';

        const control = new FormControl(answerValue, {
          validators: [Validators.required],
          updateOn: 'blur',
        });

        const answer = { control: control, id: answerId };
        this.respuestasListFormControls[newId].push(answer);

        control.valueChanges.subscribe((value) => {
          this.guardando = GUARDANDO;
          this.sendAnswer(newId, answerId, value);
        });
      });
    } else {
      // Si no hay respuestas del servidor, recrear las temporales con nuevos IDs temporales
      tempAnswers.forEach((tempAnswer) => {
        const control = new FormControl(tempAnswer.control.value, {
          validators: [Validators.required],
          updateOn: 'blur',
        });

        const tempAnswerId = 'temp' + this.tempAnswerId;
        this.tempAnswerId++;

        const answer = { control: control, id: tempAnswerId };
        this.respuestasListFormControls[newId].push(answer);

        control.valueChanges.subscribe((value) => {
          this.guardando = GUARDANDO;
          // Ahora sí puede crear la respuesta porque la pregunta ya existe
          this.sendAnswer(newId, tempAnswerId, value);
        });
      });
    }

    delete this.respuestasListFormControls[tempId];
  }

  /**
   * Maneja el cambio de tipo de pregunta
   */
  private handleQuestionTypeChange(id: any, questionType: string) {
    console.log('Cambiando tipo de pregunta:', id, questionType);

    const isTemporary = id.toString().includes('temp');

    if (questionType === 'open') {
      // Limpiar respuestas para preguntas abiertas
      this.respuestasListFormControls[id] = [];
    } else if (questionType === 'closed' || questionType === 'categorized') {
      // Asegurar respuestas mínimas para preguntas cerradas
      const currentAnswers = this.respuestasListFormControls[id] || [];
      if (currentAnswers.length < 2) {
        this.respuestasListFormControls[id] = [];
        this.createAnswer(id);
        this.createAnswer(id);
      }
    }

    // CORRECCIÓN: Manejar estado de guardado correctamente
    if (isTemporary) {
      // Para preguntas temporales
      const questionText = this.preguntasListFormControls[id].value;
      if (questionText && questionText.trim() !== '') {
        const idStr = id.toString();
        this.sendingQuestions.delete(idStr);

        setTimeout(() => {
          this.debouncedSendNewQuestion(id, questionText);
        }, 300);
      } else {
        // Si no hay texto, marcar como guardado
        this.guardando = GUARDADO;
      }
    } else {
      // Para preguntas existentes, enviar cambio al servidor
      this.sendQuestionTypeChangeToServer(id, questionType);
    }
  }

  /**
   * Crea una nueva pregunta
   */
  createQuestion() {
    const id = 'temp' + this.tempId;
    this.preguntasIds.push(id);
    this.tempId++;

    // Configurar control de pregunta
    this.setupQuestionControl(id, '');

    // Configurar control de tipo (por defecto cerrada)
    this.preguntasTypeFormControls[id] = new FormControl('closed', {
      validators: [Validators.required],
      updateOn: 'change'
    });

    // Suscribirse a cambios de tipo
    this.preguntasTypeFormControls[id].valueChanges.subscribe((value) => {
      this.guardando = GUARDANDO;
      this.handleQuestionTypeChange(id, value);
    });

    // Inicializar array de respuestas
    this.respuestasListFormControls[id] = [];

    // Crear respuestas iniciales para pregunta cerrada
    this.createAnswer(id);
    this.createAnswer(id);

    console.log('Pregunta temporal creada:', id);
  }

  /**
   * Elimina una pregunta
   */
  deleteQuestion(id: any, index: number) {
    const idStr = id.toString();

    if (idStr.includes('temp')) {
      // Pregunta temporal
      this.preguntasIds.splice(index, 1);
      delete this.preguntasListFormControls[id];
      delete this.preguntasTypeFormControls[id];
      delete this.respuestasListFormControls[id];
      this.sendingQuestions.delete(idStr);
    } else {
      // Pregunta existente
      const deleteMethod = this.useNewApi ?
        this.admin.deleteSurveyQuestionNew(id) :
        this.admin.delete_question(id);

      deleteMethod.subscribe({
        next: (data) => {
          this.preguntasIds.splice(index, 1);
          delete this.preguntasListFormControls[id];
          delete this.preguntasTypeFormControls[id];
          delete this.respuestasListFormControls[id];
          this.helper.modalAlert('¡Pregunta eliminada!');
        },
        error: (error) => {
          console.error('Error deleting question:', error);
          this.helper.modalAlert('¡Error al eliminar la pregunta!');
        }
      });
    }
  }

  /**
   * Crea una nueva respuesta
   */
  createAnswer(questionId: any) {
    const id = 'temp' + this.tempAnswerId;
    this.tempAnswerId++;

    const control = new FormControl('', {
      validators: [Validators.required],
      updateOn: 'blur',
    });

    const answer = { control: control, id: id };

    if (!this.respuestasListFormControls[questionId]) {
      this.respuestasListFormControls[questionId] = [];
    }

    this.respuestasListFormControls[questionId].push(answer);

    // CORRECCIÓN: Solo configurar suscripción para preguntas existentes
    control.valueChanges.subscribe((value) => {
      // Solo cambiar estado si realmente va a enviar algo
      if (!questionId.toString().includes('temp') && value && value.trim() !== '') {
        this.guardando = GUARDANDO;
        this.sendAnswer(questionId, id, value);
      }
      // Para preguntas temporales, no cambiar el estado de guardado
    });
  }

  /**
   * Elimina una respuesta
   */
  deleteAnswer(index: number, id: any, questionId: any) {
    this.guardando = GUARDANDO;
    const idStr = id.toString();

    if (idStr.includes('temp')) {
      // Respuesta temporal
      this.guardando = GUARDADO;
      this.respuestasListFormControls[questionId].splice(index, 1);
    } else {
      // Respuesta existente
      const deleteMethod = this.useNewApi ?
        this.admin.deleteSurveyAnswerNew(id) :
        this.admin.delete_survey_answer(id);

      deleteMethod.subscribe({
        next: (data) => {
          this.guardando = GUARDADO;
          this.respuestasListFormControls[questionId].splice(index, 1);
        },
        error: (error) => {
          console.error('Error deleting answer:', error);
          this.guardando = ERROR_GUARDADO;
        }
      });
    }
  }

  /**
   * Verifica si una pregunta es abierta
   */
  isOpenQuestion(questionId: any): boolean {
    const typeControl = this.preguntasTypeFormControls[questionId];
    return typeControl ? typeControl.value === 'open' : false;
  }

  /**
   * Verifica si una pregunta tiene respuestas opcionales
   */
  hasAnswers(questionId: any): boolean {
    const typeControl = this.preguntasTypeFormControls[questionId];
    if (!typeControl) return true;
    return typeControl.value === 'closed' || typeControl.value === 'categorized';
  }

  /**
   * Obtiene el índice de una pregunta por ID
   */
  indexByid(id: any): number {
    return this.preguntasIds.findIndex(preguntaId => preguntaId == id);
  }

  /**
   * Obtiene el control de formulario de una pregunta por ID
   */
  questionFormControlById(id: any): FormControl {
    return this.preguntasListFormControls[id];
  }

  /**
   * Obtiene el control de tipo de pregunta por ID
   */
  questionTypeFormControlById(id: any): FormControl {
    return this.preguntasTypeFormControls[id];
  }

  /**
   * Cierra el modal
   */
  hide() {
    this.modalRef.hide();
  }

  /**
   * Alterna entre nueva API y API legacy
   */
  toggleApiMode() {
    this.useNewApi = !this.useNewApi;
    console.log('API mode changed to:', this.useNewApi ? 'new' : 'legacy');
  }

  /**
   * Obtiene el estado de guardado como texto
   */
  getGuardandoText(): string {
    switch (this.guardando) {
      case GUARDANDO:
        return 'Guardando...';
      case GUARDADO:
        return 'Guardado';
      case ERROR_GUARDADO:
        return 'Error al guardar';
      default:
        return 'Desconocido';
    }
  }

  /**
   * Verifica si se puede agregar más respuestas
   */
  canAddMoreAnswers(questionId: any): boolean {
    return this.respuestasListFormControls[questionId] &&
      this.respuestasListFormControls[questionId].length < 10 &&
      this.hasAnswers(questionId);
  }

  /**
   * Verifica si se puede eliminar respuestas
   */
  canDeleteAnswers(questionId: any): boolean {
    return this.respuestasListFormControls[questionId] &&
      this.respuestasListFormControls[questionId].length > 2 &&
      this.hasAnswers(questionId);
  }
}
